import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap, Download, Printer } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const CherryPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Cherry Success: Perfect pH for Premium Fruit"
        description="Research-backed pH management for profitable cherry production. Learn how water quality affects your cherry orchard yield and quality."
        canonicalPath="/cherry"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Cherry-M.jpg"
            alt="Cherry orchard with ripe red cherries"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-pink-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🍒</span>
              <span>Premium Orchard Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Cherry Success:
              <span className="block text-pink-300">Perfect pH for Premium Fruit</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management for profitable cherry production
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-pink-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-pink-600 transition-colors inline-flex items-center space-x-3 shadow-lg"
              >
                <span>Start Reading Guide</span>
                <ArrowDown size={20} className="md:w-6 md:h-6" />
              </motion.button>

              <motion.a
                href="/cherry-ph-guide-print.html"
                target="_blank"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-pink-600 px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-gray-100 transition-colors inline-flex items-center space-x-3 shadow-lg border-2 border-pink-500"
              >
                <Printer size={20} className="md:w-6 md:h-6" />
                <span>Print Version</span>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-pink-50 to-red-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🍒</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-pink-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Sweet cherries are highly sensitive to soil pH and water quality</strong> — and your irrigation water could be quietly sabotaging your orchard's potential season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped cherry growers optimize their water quality for 40+ years. This guide explains why your irrigation water pH matters for cherries, what research shows, and how sulfur burners can protect your soil, enhance your fruit quality, and maximize your profits.
              </p>

              <div className="mt-6 p-4 bg-gradient-to-r from-pink-50 to-red-50 rounded-lg border border-pink-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">📄 Print-Optimized Version Available</h3>
                    <p className="text-sm text-gray-600">Perfect for field reference, team sharing, or adding to your sulfur burner sell sheets</p>
                  </div>
                  <a
                    href="/cherry-ph-guide-print.html"
                    target="_blank"
                    className="bg-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-pink-700 transition-colors inline-flex items-center space-x-2 shadow-md whitespace-nowrap ml-4"
                  >
                    <Printer size={18} />
                    <span>Print Guide</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Ideal Soil pH for Cherries */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Ideal Soil pH for Cherries
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-xl p-6 md:p-8 border border-pink-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-pink-500 pl-6">
                "Sweet cherries prefer well-drained soils with a slightly acidic to neutral pH, generally between 6.0 and 7.0. Soil pH outside this range can reduce nutrient availability, especially micronutrients like iron and zinc."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-pink-600" size={16} />
                <span className="text-sm font-medium text-pink-600">
                  Source: Oregon State University Extension — Sweet Cherry Nutrition and Fertilization
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">If your irrigation water is high in bicarbonates, it can raise soil pH over time — tying up nutrients, causing yellowing leaves, and hurting fruit quality.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Impact of High Bicarbonates */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: Impact of High Bicarbonates in Irrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Think of bicarbonates as tiny pH "elevators" in your water. When you irrigate with high-bicarbonate water, 
                these compounds slowly but steadily raise soil pH. It's like adding lime to your 
                cherry orchard, drop by drop, season after season. Even if your soil pH starts in the optimal range, 
                alkaline irrigation water can gradually push it above the sweet spot for cherry production.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "High bicarbonate levels in irrigation water increase soil pH, reduce infiltration rates, and can result in nutrient deficiencies for perennial tree fruit crops."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-orange-600">
                  Source: Michigan State University Extension — Irrigation Water Quality for Fruit Production
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Water testing for bicarbonate levels helps prevent soil pH creep and expensive corrective amendments later. Inline acidification can keep your water in the safe range.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Effect on Drip & Microirrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Effect on Drip & Microirrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Water quality directly affects the performance of microirrigation systems. Carbonate precipitates can clog emitters and reduce system efficiency."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-blue-600">
                  Source: University of California — Microirrigation of Trees and Vines (UC ANR Publication 3378)
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">When using drip on cherries, untreated alkaline water increases risk of plugged emitters. Inline acidification helps dissolve these minerals before they build up.</p>
              </div>

              {/* Problem vs Solution Visual */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-Bicarbonate Water Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Clogged drip emitters</li>
                    <li>• Uneven water distribution</li>
                    <li>• Reduced system efficiency</li>
                    <li>• Poor fruit quality</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Management:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform water application</li>
                    <li>• Better nutrient uptake</li>
                    <li>• Premium fruit quality</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: PNW-Specific Water Quality */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌲</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: PNW-Specific Water Quality Insight
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Tree fruit growers in the Pacific Northwest must monitor irrigation water quality, especially bicarbonate levels, to protect soil pH and orchard health long-term."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-green-600" size={16} />
                <span className="text-sm font-medium text-green-600">
                  Source: WSU Tree Fruit — Irrigation Water Quality for Tree Fruit Production
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Even if you start with good soil pH, high bicarbonate water can gradually push it up year after year — putting you at risk for iron chlorosis, poor nutrient uptake, and lower yields.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Bicarbonate Threshold */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Recommended Bicarbonate Threshold
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-purple-500 pl-6">
                "When bicarbonate levels exceed 120 ppm in irrigation water, it's recommended to use acidification to prevent soil alkalinity and infiltration problems."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-purple-600" size={16} />
                <span className="text-sm font-medium text-purple-600">
                  Source: UCANR — Agricultural Salinity and Drainage (FAO Bulletin)
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Neutralizing bicarbonates protects your soil pH, orchard uniformity, and fruit quality — season after season.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Cherries:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents emitter clogging in drip systems',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in cherry orchards'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Cherries" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CherryPage;